/**
 * PassageCard component
 * Displays the reading passage in a collapsible card with warm earth tone theme
 * Starts expanded by default, can be collapsed to save screen space
 *
 * Redesign (2026-06): warm cream card, serif (font-reading) body to match the desktop
 * "paper sheet" treatment. Collapse behaviour and the "Reading Passage" heading are preserved.
 *
 * IMPORTANT — line-numbered passages: PSLE passages embed line-number prefixes at fixed
 * physical lines; the text is rendered with `whitespace-pre` (no wrap) so those line
 * references never reflow. Long lines scroll horizontally instead of wrapping.
 *
 * Highlighter (2026-07): opt-in via `highlightSessionId`. Without it this renders exactly as
 * before — a single plain <p>. Note this card and PassagePanel are BOTH mounted at every
 * viewport (they hide each other with CSS, not by unmounting), so their highlight state is
 * shared through a module-level store rather than component state.
 */

import { useState } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { cn } from '@/lib/utils';
import { usePassageHighlights } from '@/hooks/usePassageHighlights';
import { usePassageAnnotations } from '@/hooks/usePassageAnnotations';
import { PassageToolbars } from '@/components/PassageToolbars';
import { HighlightablePassageText } from '@/components/HighlightablePassageText';
import { usePassageCopyGuard } from '@/lib/passageCopyGuard';

export interface PassageCardProps {
  /** Reading passage text to display */
  passage: string;
  /**
   * Enables the highlighter, scoped to this session. Omit for a read-only passage.
   * Highlights persist in localStorage per session; they are not shared across devices.
   */
  highlightSessionId?: string;
}

/**
 * PassageCard - Displays collapsible reading passage with ThinkOtter earth tone theme
 */
export function PassageCard({ passage, highlightSessionId }: PassageCardProps) {
  const [isExpanded, setIsExpanded] = useState(true);
  // Called before the early return: hooks must run unconditionally. Inert without a sessionId.
  const highlights = usePassageHighlights(highlightSessionId, passage);
  const annotations = usePassageAnnotations(highlightSessionId, passage);
  const { ref: copyGuardRef, guardProps } = usePassageCopyGuard<HTMLDivElement>();

  // Don't render if passage is empty or undefined
  if (!passage || passage.trim() === '') {
    return null;
  }

  return (
    <Card
      className="mb-4 overflow-hidden rounded-3xl border-otter-sand p-4 shadow-sm md:p-6"
      style={{ background: 'linear-gradient(180deg, #fffdf9, #fdf6ea)' }}
    >
      <div className="mb-2 flex items-start justify-between">
        <div className="flex items-center gap-1.5">
          <span aria-hidden="true" className="text-base">
            📖
          </span>
          <h2 className="font-display text-lg font-semibold text-otter-earth-800">Reading Passage</h2>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setIsExpanded(!isExpanded)}
          className="min-h-11 min-w-11 rounded-xl text-otter-earth-500 hover:bg-otter-sand"
          aria-label={isExpanded ? 'Collapse passage' : 'Expand passage'}
        >
          {isExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
        </Button>
      </div>

      {/* Toolbar sits outside the collapsible wrapper so it does not eat into its max-height. */}
      {highlights.enabled && isExpanded && (
        <PassageToolbars
          armedShape={annotations.armedShape}
          onArmShape={annotations.setArmedShape}
          onUndoMark={annotations.undo}
          onClearMarks={annotations.clearAll}
          hasMarks={annotations.marks.length > 0}
          armedColor={highlights.armedColor}
          onArmColor={highlights.setArmedColor}
          onClearHighlights={highlights.clearAll}
          hasHighlights={highlights.ranges.length > 0}
        />
      )}

      <div
        className={cn(
          'overflow-hidden transition-all duration-200 ease-in-out',
          isExpanded ? 'max-h-64 opacity-100' : 'max-h-0 opacity-0'
        )}
      >
        {/* Line-numbered passage: preserve exact lines (no wrap); scroll long lines.
            The copy guard registers this element so it covers both the plain and highlightable
            branches; selection itself stays untouched. */}
        <div
          ref={copyGuardRef}
          className="max-h-48 overflow-auto font-reading text-base leading-[1.7] text-otter-earth-800 md:max-h-64"
          {...guardProps}
        >
          {highlights.enabled ? (
            <HighlightablePassageText
              tokens={highlights.tokens}
              ranges={highlights.ranges}
              armedColor={highlights.armedColor}
              onHighlightRange={highlights.highlightRange}
              onEraseAt={highlights.eraseAt}
              className="whitespace-pre"
              annotations={{
                armedShape: annotations.armedShape,
                marks: annotations.marks,
                pendingLink: annotations.pendingLink,
                onMarkRange: annotations.markRange,
                onEraseMarkAt: annotations.eraseAt,
              }}
            />
          ) : (
            <p className="whitespace-pre">{passage}</p>
          )}
        </div>
      </div>
    </Card>
  );
}
